import { useCallback, useEffect, useRef, useState } from 'react';

import { getCommissionSummary, getCommissions } from '../../commission/api/commissions.api';
import type { Commission, CommissionSummary } from '../../commission/types/commission.types';

export function useTenantCommission(tenantId: number | undefined) {
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [loading, setLoading] = useState(Boolean(tenantId));
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);

  const loadCommission = useCallback(() => {
    const generation = ++requestGenerationRef.current;
    const isCurrentRequest = () =>
      mountedRef.current && requestGenerationRef.current === generation;

    if (tenantId === undefined) {
      setCommissions([]);
      setSummary(null);
      setLoading(false);
      setError(null);
      return Promise.resolve();
    }

    setLoading(true);
    setError(null);

    const params = { tenantId: String(tenantId) };
    return Promise.all([
      getCommissions(1, 8, params),
      getCommissionSummary(params),
    ])
      .then(([page, nextSummary]) => {
        if (!isCurrentRequest()) return;
        setCommissions(page.commissions);
        setSummary(nextSummary);
      })
      .catch(() => {
        if (isCurrentRequest()) setError('خطا در دریافت کمیسیون‌های این تننت');
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });
  }, [tenantId]);

  useEffect(() => {
    mountedRef.current = true;
    void loadCommission();
    return () => {
      mountedRef.current = false;
    };
  }, [loadCommission]);

  return { commissions, summary, loading, error, refetch: loadCommission };
}
